import React, { useState } from 'react';
import axios from 'axios';
import {
  Mail,
  Phone,
  MapPin,
  Send,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ArrowUpRight,
} from 'lucide-react';
import { COMPANY } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState({ state: 'idle', msg: '' });

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: 'loading', msg: '' });
    try {
      await axios.post(`${API}/contact`, form);
      setStatus({ state: 'success', msg: 'Message sent. We will be in touch shortly.' });
      setForm({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setStatus({ state: 'idle', msg: '' }), 6000);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        (Array.isArray(err?.response?.data) ? err.response.data[0]?.msg : null) ||
        'Something went wrong. Please try again.';
      setStatus({ state: 'error', msg: typeof detail === 'string' ? detail : 'Please check the form.' });
    }
  };

  const loading = status.state === 'loading';

  return (
    <section id="contact" className="relative py-24 lg:py-32 bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-[12px] font-medium text-slate-700 uppercase tracking-[0.18em]">
              Contact
            </div>
            <h2 className="mt-4 font-display text-[36px] md:text-[52px] leading-[1.05] font-semibold tracking-tight text-slate-900">
              Let’s talk about your{' '}
              <span className="bg-gradient-to-r from-indigo-600 to-violet-500 bg-clip-text text-transparent">
                hardest problem.
              </span>
            </h2>
            <p className="mt-5 text-[16px] leading-relaxed text-slate-600 max-w-md">
              Tell us what’s on your mind. We’ll come back within one working day with either sharp follow-up questions, an intro call slot, or an honest “not us - try these folks”.
            </p>

            <div className="mt-10 space-y-4">
              <a
                href={`mailto:${COMPANY.email}`}
                className="group flex items-center gap-4 rounded-2xl border border-slate-200 bg-white px-5 py-4 hover:border-slate-300 transition-colors"
              >
                <span className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
                  <Mail size={17} className="text-indigo-600" />
                </span>
                <div className="flex-1">
                  <div className="text-[12.5px] text-slate-500">Email</div>
                  <div className="text-[14.5px] font-medium text-slate-900">{COMPANY.email}</div>
                </div>
                <ArrowUpRight size={16} className="text-slate-400 group-hover:text-slate-700 transition-colors" />
              </a>
              <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white px-5 py-4">
                <span className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                  <Phone size={17} className="text-emerald-600" />
                </span>
                <div>
                  <div className="text-[12.5px] text-slate-500">Phone</div>
                  <div className="text-[14.5px] font-medium text-slate-900">{COMPANY.phone}</div>
                </div>
              </div>
              <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white px-5 py-4">
                <span className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                  <MapPin size={17} className="text-amber-600" />
                </span>
                <div>
                  <div className="text-[12.5px] text-slate-500">Headquarters</div>
                  <div className="text-[14.5px] font-medium text-slate-900">{COMPANY.location}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              className="relative rounded-3xl border border-slate-200 bg-white p-6 md:p-10 shadow-xl shadow-slate-900/5"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-[12.5px] font-medium text-slate-700 mb-1.5">
                    Your name
                  </label>
                  <input
                    required
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    disabled={loading}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-[14.5px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/25 focus:border-indigo-500 focus:bg-white transition-colors disabled:opacity-60"
                    placeholder="Jane Doe"
                  />
                </div>
                <div>
                  <label className="block text-[12.5px] font-medium text-slate-700 mb-1.5">
                    Work email
                  </label>
                  <input
                    required
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    disabled={loading}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-[14.5px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/25 focus:border-indigo-500 focus:bg-white transition-colors disabled:opacity-60"
                    placeholder="jane@company.com"
                  />
                </div>
              </div>
              <div className="mt-5">
                <label className="block text-[12.5px] font-medium text-slate-700 mb-1.5">
                  Topic
                </label>
                <input
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                  disabled={loading}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-[14.5px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/25 focus:border-indigo-500 focus:bg-white transition-colors disabled:opacity-60"
                  placeholder="Chaos engineering, LLM automation, modernization…"
                />
              </div>
              <div className="mt-5">
                <label className="block text-[12.5px] font-medium text-slate-700 mb-1.5">
                  What’s on your mind?
                </label>
                <textarea
                  required
                  name="message"
                  rows={5}
                  value={form.message}
                  onChange={handleChange}
                  disabled={loading}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-[14.5px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/25 focus:border-indigo-500 focus:bg-white transition-colors resize-none disabled:opacity-60"
                  placeholder="A bit about your stack, team size, and what you’d like to change."
                />
              </div>
              <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-xl font-medium hover:bg-slate-800 transition-colors disabled:opacity-70"
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      Send message <Send size={15} />
                    </>
                  )}
                </button>
                {status.state === 'success' && (
                  <span className="inline-flex items-center gap-2 text-emerald-600 font-medium text-[14px]">
                    <CheckCircle2 size={17} /> {status.msg}
                  </span>
                )}
                {status.state === 'error' && (
                  <span className="inline-flex items-center gap-2 text-red-600 font-medium text-[14px]">
                    <AlertCircle size={17} /> {status.msg}
                  </span>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
